import React, { ComponentType, ReactNode, useState, useEffect } from 'react';

import { Box } from '@material-ui/core';
import PropTypes from 'prop-types';

import { TabsComponent } from 'components/TabsComponent/TabsComponent';
import { useAuthState } from 'contexts/AuthContext';

interface Errors {
  [key: string]: boolean;
}

export interface ComponentWithPageLanguageProps {
  pageLanguageTabValue?: number;
  handleValidationCheck?: (langCode: string, fieldName: string) => (isValid: boolean) => void;
  children?: ReactNode;
}

export const withPageLanguage =
  <T,>(Component: ComponentType<T & ComponentWithPageLanguageProps>) =>
  (props: T): JSX.Element => {
    const {
      currentUser: {
        attributes: { clientLanguages },
      },
    } = useAuthState();
    const [tabValue, setTabValue] = useState(0);
    const [errors, setErrors] = useState<Errors>({});
    const [errorTabs, setErrorTabs] = useState<string[]>([]);

    useEffect(() => {
      const invalidLangs = Object.entries(errors).reduce((acc, [key, isError]) => {
        const lang = key.split('.')[0];

        return isError ? acc.add(lang) : acc;
      }, new Set<string>());

      setErrorTabs(Array.from(invalidLangs));
    }, [errors]);

    const handleValidationCheck = (langCode: string, fieldName: string) => (isValid: boolean) =>
      setErrors(prev => ({ ...prev, [`${langCode}.${fieldName}`]: !isValid }));

    return clientLanguages?.length > 1 ? (
      <Component {...props} pageLanguageTabValue={tabValue} handleValidationCheck={handleValidationCheck}>
        <Box mb={3}>
          <TabsComponent
            value={tabValue}
            setValue={setTabValue}
            tabNames={clientLanguages}
            errorTabs={errorTabs}
            tabClassName="lang"
          />
        </Box>
      </Component>
    ) : (
      <Component {...props} />
    );
  };

export const withPageLanguagePropTypes = {
  pageLanguageTabValue: PropTypes.number,
  handleValidationCheck: PropTypes.func,
  children: PropTypes.oneOfType([PropTypes.element, PropTypes.shape({}), PropTypes.array]),
};

export const withPageLanguageDefaultProps = {
  pageLanguageTabValue: 0,
  handleValidationCheck: () => {},
  children: null,
};
